import { useEffect, useState } from "react";
import { useSubscription } from "../../context/SubscriptionContext";
import { Clock } from "lucide-react";

/**
 * TrialCountdownBadge — a persistent header badge showing remaining trial days.
 *
 * Color scheme:
 *   > 7 days  → amber/orange
 *   ≤ 7 days  → red (urgency)
 *   ≤ 0 days  → red, "Trial Expired"
 *   not trial → renders nothing
 */
export default function TrialCountdownBadge() {
  const { isTrial, trialDaysLeft } = useSubscription();
  const [pulse, setPulse] = useState(false);

  // Subtle pulse animation on mount for visibility
  useEffect(() => {
    if (!isTrial) return;
    setPulse(true);
    const timer = setTimeout(() => setPulse(false), 2000);
    return () => clearTimeout(timer);
  }, [isTrial]);

  // Hide the badge unconditionally for Dev and Test
  const isHidden = true;
  if (isHidden) return null;

  const isExpired = trialDaysLeft <= 0;
  const isUrgent = trialDaysLeft <= 7 && !isExpired;

  // Color classes
  const bgClass = isExpired || isUrgent
    ? "bg-red-50 border-red-200"
    : "bg-amber-50 border-amber-200";

  const textClass = isExpired || isUrgent
    ? "text-red-700"
    : "text-amber-700";

  const iconClass = isExpired || isUrgent
    ? "text-red-500"
    : "text-amber-500";

  const dotClass = isExpired || isUrgent
    ? "bg-red-500"
    : "bg-amber-500";

  const label = isExpired
    ? "Trial Expired"
    : `${trialDaysLeft} Day${trialDaysLeft !== 1 ? "s" : ""} Left in Trial`;

  return (
    <div
      className={`
        inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1
        text-xs font-semibold select-none transition-all duration-300
        ${bgClass}
        ${pulse ? "scale-105 shadow-md" : "shadow-sm hover:shadow-md"}
      `}
      title={isExpired ? "Your free trial has ended" : `${trialDaysLeft} days remaining in your free trial`}
    >
      <Clock className={`h-3 w-3 ${iconClass}`} />
      {!isExpired && (
        <span className={`h-1.5 w-1.5 rounded-full ${dotClass} animate-pulse`} />
      )}
      <span className={textClass}>{label}</span>
    </div>
  );
}
